import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { ModalsManager } from '@/components/modals/modalsManager';
import { Badge, Button } from '@/components/ui';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { AutoReloadModal } from '@/features/subscription/auto-reload-modal';
import { BuyUsageCreditsModal } from '@/features/subscription/buy-usage-credits-modal';

import { subscriptionApis, useCreditBalanceQuery } from './subscription.query';
import { subscriptionStore } from './subscription.store';
import { type AutoRefillSettings } from './subscription.types';
import { creditsToValueCents, formatCentsExact } from './usage-format';

export const CreditPacksSection = observer(() => {
  const paymentMethod = subscriptionStore.state.paymentMethod;
  const isTeamMember = subscriptionStore.state.isTeamMember;
  const autoRefill = subscriptionStore.state.autoRefill;

  const { data: creditBalance } = useCreditBalanceQuery();
  const { data: packs } = useQuery({
    queryKey: ['credit-packs'],
    queryFn: subscriptionApis.getCreditPacks,
  });

  const balance = creditBalance?.purchased_balance;
  const rateCentsPer1000 = creditBalance?.rate_cents_per_1000;
  const maxDiscountPct = Math.max(
    0,
    ...(packs?.discount_brackets ?? []).map((bracket) => bracket.discount_pct)
  );

  return (
    <>
      <XStack className="items-center justify-between gap-4">
        <YStack className="gap-0.5">
          <span className="text-xl font-semibold tabular-nums text-foreground">
            {balance != null && rateCentsPer1000 != null
              ? formatCentsExact(creditsToValueCents(balance, rateCentsPer1000))
              : '—'}
          </span>
          <span className="text-sm text-foreground-muted">
            Current balance · Spent after your monthly usage runs out
          </span>
        </YStack>
        <Button
          variant="outline"
          className="shrink-0 gap-2"
          onClick={() => ModalsManager.showResolvable(BuyUsageCreditsModal, {})}
        >
          Buy usage credits
          {maxDiscountPct > 0 && (
            <Badge variant="default" size="xs">
              Up to {maxDiscountPct}% off
            </Badge>
          )}
        </Button>
      </XStack>

      {!isTeamMember && paymentMethod && (
        <AutoReloadRow autoRefill={autoRefill} />
      )}
    </>
  );
});

const AutoReloadRow = ({ autoRefill }: { autoRefill: AutoRefillSettings }) => {
  const [busy, setBusy] = useState(false);

  const thresholdValueCents = autoRefill.threshold_value_cents ?? 0;
  const topupValueCents = autoRefill.topup_value_cents ?? 0;

  const openModal = () =>
    ModalsManager.showResolvable(AutoReloadModal, {
      initialThresholdValueCents: autoRefill.threshold_value_cents ?? undefined,
      initialTopupValueCents: autoRefill.topup_value_cents ?? undefined,
      initialMonthlyCapDollars: autoRefill.monthly_cap_cents
        ? autoRefill.monthly_cap_cents / 100
        : undefined,
    });

  const turnOff = async () => {
    setBusy(true);
    await subscriptionApis.updateAutoRefill({ enabled: false });
    setBusy(false);
  };

  return (
    <XStack className="items-center justify-between gap-3 pt-4 border-t border-border">
      <YStack className="gap-0.5">
        <span className="text-sm font-medium text-foreground">Auto-reload</span>
        <span className="text-sm text-foreground-muted">
          {autoRefill.enabled
            ? `When usage drops below ${formatCentsExact(thresholdValueCents)}, top up with ${formatCentsExact(topupValueCents)}.`
            : "Automatically buy more usage credits when you're running low"}
        </span>
        {autoRefill.last_failure_reason && (
          <span className="text-xs text-destructive">
            {autoRefill.last_failure_reason === 'Monthly recharge cap reached'
              ? 'Monthly limit reached. Auto-reload resumes next month.'
              : "Your last refill didn't go through. Update your payment method and try again."}
          </span>
        )}
      </YStack>
      <XStack className="gap-2 shrink-0">
        {autoRefill.enabled && (
          <Button
            variant="outline"
            size="sm"
            onClick={turnOff}
            isLoading={busy}
            disabled={busy}
          >
            Turn off
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={openModal} disabled={busy}>
          {autoRefill.enabled ? 'Edit' : 'Turn on'}
        </Button>
      </XStack>
    </XStack>
  );
};
